import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, Search, Loader2, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import RepararVentas from "@/components/admin/RepararVentas";

const CAJAS = ["Caja 01", "Caja 02", "Caja 03"];
const mxn = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

export default function ReasignacionVentas({ sucursales }) {
  const [activa, setActiva] = useState(false);
  const [folio, setFolio] = useState("");
  const [venta, setVenta] = useState(null);
  const [dest, setDest] = useState({ sucursal_id: "", caja: "", motivo: "" });
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);
  const nombre = (id) => sucursales.find((s) => s.id === id)?.nombre || "—";

  const cargar = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.get("/config"), api.get("/ventas/reasignaciones")]);
      setActiva(!!c.data.reasignacion_ventas_activa); setAudit(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function toggle() {
    try { const { data } = await api.put("/config/pilotaje", { reasignacion_ventas_activa: !activa }); setActiva(data.reasignacion_ventas_activa); toast.success(data.reasignacion_ventas_activa ? "Reasignación de ventas ACTIVADA (uso de pilotaje)" : "Reasignación de ventas desactivada"); } catch (e) { toast.error(formatApiError(e)); }
  }
  async function buscar(e) {
    e.preventDefault();
    setVenta(null);
    try {
      const { data } = await api.get("/ventas", { params: { q: folio.trim(), limite: 5 } });
      const v = data.find((x) => x.folio?.toLowerCase() === folio.trim().toLowerCase()) || data[0];
      if (!v) return toast.error("No se encontró la venta");
      setVenta(v); setDest({ sucursal_id: v.sucursal_id || "", caja: v.caja || "Caja 01", motivo: "" });
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function reasignar() {
    const ok = window.confirm(`¿Confirmas mover la venta ${venta.folio} (${mxn(venta.total)})\nde ${nombre(venta.sucursal_id)} · ${venta.caja}\na ${nombre(dest.sucursal_id)} · ${dest.caja}?\n\nEl stock descontado se devolverá en origen y se descontará en destino. Quedará registro de auditoría.`);
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/ventas/${venta.id}/reasignar`, { ...dest, confirmar: true });
      toast.success(`${venta.folio} reasignada a ${data.auditoria.sucursal_destino} · ${data.auditoria.caja_destino}`);
      setVenta(null); setFolio(""); cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  return (
    <section className="surface-card p-5 space-y-4" data-testid="reasignacion-ventas">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-brand-ink flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-brand-amber" /> Transferencia de ventas entre sucursales <span className="chip chip-red h-5 px-1.5 text-[10px]">pilotaje</span></h2>
          <p className="text-xs text-brand-muted">Corrige ventas del pilotaje que quedaron en la sucursal o caja incorrecta. Solo Gran Admin; cada movimiento queda auditado. Al terminar, apágala y desaparece de la interfaz.</p>
        </div>
        <button data-testid="reasignacion-switch" onClick={toggle} className={`px-3 h-9 rounded-full text-xs font-bold ${activa ? "bg-rose-600 text-white" : "bg-brand-surface text-brand-slate"}`}>{activa ? "Activada · desactivar" : "Desactivada · activar"}</button>
      </div>

      {activa && (
        <>
          <form onSubmit={buscar} className="flex gap-2 items-end flex-wrap">
            <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-3 text-brand-muted" /><input data-testid="reasignacion-folio-input" value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="Folio de la venta (RB-000123)" className="field pl-9" /></div>
            <button data-testid="reasignacion-buscar-btn" type="submit" className="btn-ink h-10 px-4 text-sm">Buscar</button>
          </form>
          {venta && (
            <div className="rounded-2xl border border-brand-line p-4 space-y-3" data-testid="reasignacion-venta-card">
              <div className="text-sm"><strong>{venta.folio}</strong> · {mxn(venta.total)} · {new Date(venta.created_at).toLocaleString("es-MX")} · actualmente en <strong>{nombre(venta.sucursal_id)}</strong> · {venta.caja}{venta.reasignada && <span className="chip chip-amber h-5 px-1.5 text-[10px] ml-2">ya reasignada antes</span>}</div>
              <div className="grid sm:grid-cols-3 gap-2">
                <select data-testid="reasignacion-sucursal-select" value={dest.sucursal_id} onChange={(e) => setDest({ ...dest, sucursal_id: e.target.value })} className="field">{sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}</select>
                <select data-testid="reasignacion-caja-select" value={dest.caja} onChange={(e) => setDest({ ...dest, caja: e.target.value })} className="field">{CAJAS.map((c) => <option key={c}>{c}</option>)}</select>
                <input data-testid="reasignacion-motivo-input" value={dest.motivo} onChange={(e) => setDest({ ...dest, motivo: e.target.value })} placeholder="Motivo (opcional)" className="field" />
              </div>
              <button data-testid="reasignacion-ejecutar-btn" disabled={busy || (dest.sucursal_id === venta.sucursal_id && dest.caja === venta.caja)} onClick={reasignar} className="btn-amber h-11 px-5 text-sm disabled:opacity-40">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />} Reasignar con confirmación</button>
            </div>
          )}
        </>
      )}

      <RepararVentas onDone={cargar} />

      {audit.length > 0 && (
        <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="reasignacion-auditoria">
          <thead><tr className="table-head"><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Venta</th><th className="px-3 py-2">Origen</th><th className="px-3 py-2">Destino</th><th className="px-3 py-2">Usuario</th><th className="px-3 py-2">Motivo</th></tr></thead>
          <tbody>{audit.map((a) => <tr key={a.id} className="border-t border-brand-line/60" data-testid={`reasignacion-row-${a.id}`}><td className="px-3 py-2 whitespace-nowrap">{new Date(a.created_at).toLocaleString("es-MX")}</td><td className="px-3 py-2 font-mono">{a.folio} · {mxn(a.total)}</td><td className="px-3 py-2">{a.sucursal_origen} · {a.caja_origen}</td><td className="px-3 py-2 font-semibold">{a.sucursal_destino} · {a.caja_destino}</td><td className="px-3 py-2">{a.usuario_nombre}</td><td className="px-3 py-2 text-brand-muted">{a.motivo || "—"}</td></tr>)}</tbody>
        </table></div>
      )}
    </section>
  );
}
